import { existsSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { forComponent } from '../src/logging';

const log = forComponent('cli-util');

/**
 * Shared CLI helpers — dot-dir resolution, text truncation, safe JSON. One home for the
 * patterns that were previously re-implemented per call site (core.ts dots() vs cli.ts adots(),
 * ad-hoc slice(0,N) labels, bare try/JSON.parse).
 */

/** The `.agent`/`.claude` config-dir search order for `sub` (first wins on name collision):
 *  project .agent → project .claude → user ~/.agent → user ~/.claude. Home-level dirs are what
 *  surface a GLOBAL skill/command everywhere. `existing` filters to dirs present on the real disk
 *  (use only when `base` is a real path — VFS callers pass the unfiltered list). */
export function dotDirs(base: string, sub: string, opts: { existing?: boolean; home?: string } = {}): string[] {
  const home = opts.home ?? homedir();
  const dirs = [`${base}/.agent/${sub}`, `${base}/.claude/${sub}`, `${home}/.agent/${sub}`, `${home}/.claude/${sub}`];
  return opts.existing ? dirs.filter((d) => existsSync(d)) : dirs;
}

/** Cap `s` at `n` chars with a suffix marker. Null-safe. */
export function truncate(s: string | null | undefined, n: number, suffix = '…'): string {
  const t = s ?? '';
  return t.length > n ? t.slice(0, n) + suffix : t;
}

/** One-line label: collapse whitespace/newlines, trim, cap at `max` (checkpoint labels, commit subjects). */
export function sanitizeLabel(s: string, max = 60): string {
  return truncate(s.replace(/\s+/g, ' ').trim(), max, '');
}

/** JSON.parse that returns `fallback` instead of throwing — and LOGS the failure (never silently
 *  swallow: a corrupt settings/session file should leave a trace). */
export function parseJson<T = any>(text: string, fallback: T, what = 'json'): T {
  try { return JSON.parse(text); } catch (e) {
    log.debug(`parseJson(${what}) failed: ${(e as Error).message}`);
    return fallback;
  }
}

/** Read + parse a JSON file from the real disk; `fallback` when absent or malformed (logged). */
export function readJsonFile<T = any>(path: string, fallback: T): T {
  if (!existsSync(path)) return fallback;
  let text: string;
  try { text = readFileSync(path, 'utf8'); } catch (e) {
    log.debug(`readJsonFile(${path}) unreadable: ${(e as Error).message}`);
    return fallback;
  }
  return parseJson(text, fallback, path);
}
